/**
 * A failure is not a fact.
 *
 * A read that failed is not permission to state a negative. `[]` means "there are none"; a refusal,
 * a timeout or a restart means "we could not tell" — and a screen that renders the second as the
 * first is lying to the person reading it. This module exists so a caller can carry the difference
 * instead of flattening it.
 *
 * WHY A THIRD FILE RATHER THAN A THIRD DIALECT. Two correct halves already existed and neither was
 * whole:
 *
 *   * `modules/fleet/api/errorMessage.js` (`apiErrorMessage`) knows how to make a failure SAFE TO
 *     RENDER. FastAPI's 422 `detail` is an array of objects; handed to JSX it throws "Objects are
 *     not valid as a React child" and unmounts the page. Every branch here ends at a string.
 *   * `modules/hr/components/FieldAccessPanel.jsx` (`describeError`) knows the right AXIS: split by
 *     what the reader should DO, not by what went wrong. A 403 is a boundary and retrying is
 *     pointless; a 503 is a hiccup and retrying is the whole answer.
 *
 * Both now delegate here and keep their exact previous output, so their existing call sites are
 * untouched.
 *
 * WHAT IT DOES NOT DO. It does not decide what a screen says — only what KIND of failure happened
 * and the best sentence available for it. The wording lives at the call site, which is the only
 * place that knows what "it" was.
 */

import { API_ERRORS } from './config'

/**
 * The kinds, in the order a caller should branch on them.
 *
 * `transient` is the ONLY kind that earns a Retry control. That is deliberate and it was checked
 * rather than assumed: a survey of all 179 `HTTP_403_FORBIDDEN` raises in this backend found not
 * one that an immediate retry could clear. Permissions, tenant and employee link are all JWT
 * CLAIMS (shared/auth/dependencies.py:219-223) read without a database round-trip, so a 403 is a
 * property of the token in hand, not of the moment. The transient cases the backend does have are
 * deliberately given a DIFFERENT code — shared/uploads/content.py:192 answers "still being scanned"
 * with a 409, reserving 403 for the terminal refusal.
 *
 * The one honest caveat, which belongs in the COPY and never in a button: `/auth/refresh` re-mints
 * permissions from the database every 15 minutes (identity settings.py:90, auth.py:637), so a
 * permission granted elsewhere does reach this tab eventually. "You cannot do this" is true now;
 * "you can never do this" is not. Word boundary messages accordingly.
 */
export const FAILURE_KINDS = Object.freeze({
  SIGNED_OUT: 'signedOut',
  BOUNDARY: 'boundary',
  MISSING: 'missing',
  CONFLICT: 'conflict',
  TRANSIENT: 'transient',
  ERROR: 'error',
})

/**
 * Anything a server calls a "detail", as a string that cannot crash a render.
 *
 * `withFields` selects between the two flattenings that already exist in the tree, because each is
 * right for its own reader and unifying them would silently change three screens:
 *
 *   true  — fleet's: "cost_date: Field required". Names the field the server objected to, which is
 *           the part that makes a validation failure actionable on a FORM.
 *   false — FieldAccessPanel's: "Field required". A panel reporting somebody else's access has no
 *           field to point at, and a `body.` prefix would be noise.
 *
 * Returns '' when there is nothing to say, so the caller falls through to its own fallback rather
 * than rendering an empty banner.
 */
export function flattenDetail(detail, withFields = true) {
  if (detail === null || detail === undefined) return ''
  if (typeof detail === 'string') return detail

  if (Array.isArray(detail)) {
    if (!detail.length) return ''
    if (!withFields) {
      // FieldAccessPanel's shape exactly: messages only, empties dropped.
      const msgs = detail.map((d) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean)
      return msgs.length ? msgs.join('; ') : ''
    }
    // fleet's shape exactly, including the 'is invalid' it substitutes for an element carrying no
    // msg at all. `body` is stripped from `loc` because every field in a request body is prefixed
    // with it and repeating it adds nothing.
    return detail
      .map((d) => {
        if (typeof d === 'string') return d
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : ''
        const msg = d?.msg ?? 'is invalid'
        return field ? `${field}: ${msg}` : msg
      })
      .join('; ')
  }

  if (typeof detail === 'object') return detail.msg || detail.message || ''
  return String(detail)
}

/** The kind alone, for a caller that already has its own wording. */
export function failureKind(err) {
  const status = err?.response?.status

  // No response at all: the request never landed. A timeout, a dropped connection, a service
  // restarting mid-flight. Retrying is exactly the right thing to offer.
  if (!status) return FAILURE_KINDS.TRANSIENT

  // A 401 the client kept the session through (the sign-in service was busy, not refusing): the
  // person is still signed in, and the next press will work. And a 429 is "busy" by definition.
  if (err?.sessionKept || status === 429) return FAILURE_KINDS.TRANSIENT

  if (status === 401) return FAILURE_KINDS.SIGNED_OUT
  if (status === 403) return FAILURE_KINDS.BOUNDARY
  if (status === 404) return FAILURE_KINDS.MISSING
  if (status === 409) return FAILURE_KINDS.CONFLICT

  // 502/503/504 are the gateway saying the service is not there right now. A 500 is a bug in a
  // service that IS there — it will not answer differently one second later, so it gets no Retry.
  if (status === 502 || status === 503 || status === 504) return FAILURE_KINDS.TRANSIENT

  return FAILURE_KINDS.ERROR
}

/**
 * The best sentence available for a failure.
 *
 * THE SERVER'S OWN SENTENCE WINS. The shared interceptor flattens every 403 to "You do not have
 * permission to perform this action", and in this app that is usually the WORSE sentence: the
 * backend writes one that names the permission and says who to ask ("Your account does not have
 * permission to do this (finance:view_invoices). Ask an administrator if you need it."). The two
 * screens that already prefer `detail` are the two that read best, so that is the rule here.
 *
 * The exception is 401, where `detail` is always machine-facing ("Invalid or expired token") and
 * the reader's next action is the same either way, so the friendlier constant wins.
 */
export function failureMessage(err, fallback = '', withFields = true) {
  const status = err?.response?.status
  // Not "your session has expired" to somebody who is still signed in (see failureKind).
  if (err?.sessionKept) return err.userMessage || 'The server is busy. Please try again in a moment.'
  if (status === 401) return API_ERRORS.UNAUTHORIZED

  const detail = flattenDetail(err?.response?.data?.detail, withFields)
  if (detail) return detail

  const body = err?.response?.data
  if (body && typeof body === 'object' && typeof body.message === 'string' && body.message) {
    return body.message
  }

  // `userMessage` is whatever an interceptor computed. It is a string on every path that sets one,
  // but this is the last gate before a render, so an array that got here anyway is flattened rather
  // than thrown at JSX.
  const carried = err?.userMessage
  if (typeof carried === 'string' && carried) return carried
  if (carried) {
    const flat = flattenDetail(carried, withFields)
    if (flat) return flat
  }

  if (fallback) return fallback

  switch (failureKind(err)) {
    case FAILURE_KINDS.SIGNED_OUT: return API_ERRORS.UNAUTHORIZED
    case FAILURE_KINDS.BOUNDARY:   return API_ERRORS.FORBIDDEN
    case FAILURE_KINDS.MISSING:    return API_ERRORS.NOT_FOUND
    case FAILURE_KINDS.CONFLICT:   return API_ERRORS.CONFLICT
    case FAILURE_KINDS.TRANSIENT:
      // A 502/503/504 is transient, but the SERVER answered it. Saying "check your internet
      // connection" there blames the reader's phone for a gateway that is up enough to reply, and
      // sends them to fix something that is not broken. Only a failure with no response at all gets
      // the network wording.
      if (err?.response) return API_ERRORS.SERVER_ERROR
      return err?.code === 'ECONNABORTED' ? API_ERRORS.TIMEOUT : API_ERRORS.NETWORK_ERROR
    default:
      return API_ERRORS.SERVER_ERROR
  }
}

/**
 * A failure, described: `{ kind, message, status, retryable }`.
 *
 * `retryable` is `kind === 'transient'` and nothing else — see the note on FAILURE_KINDS. Offer a
 * Retry control if and only if it is true; a control that cannot succeed is a control that fails.
 */
export function describeFailure(err, fallback = '', options = undefined) {
  const withFields = options?.fields !== false
  const kind = failureKind(err)
  return {
    kind,
    message: failureMessage(err, fallback, withFields),
    status: err?.response?.status ?? null,
    retryable: kind === FAILURE_KINDS.TRANSIENT,
  }
}

/**
 * The string an axios response interceptor should hang on `error.userMessage`.
 *
 * This is the mapping from `shared/api/client.js` with the SESSION SIDE EFFECTS left out, so a
 * per-module client can attach the same message without also inheriting the shared client's
 * 401-clears-auth-and-redirects behaviour, which those clients deliberately do not want (see the
 * note in modules/timekeeping/api/timekeeping.js).
 */
export function userMessageFor(error) {
  const response = error?.response
  if (!response) {
    return error?.code === 'ECONNABORTED' ? API_ERRORS.TIMEOUT : API_ERRORS.NETWORK_ERROR
  }

  const { status, data } = response
  switch (status) {
    case 401: return API_ERRORS.UNAUTHORIZED
    case 403: return API_ERRORS.FORBIDDEN
    case 404: return API_ERRORS.NOT_FOUND
    case 409: return flattenDetail(data?.detail) || API_ERRORS.CONFLICT
    case 500:
    case 502:
    case 503:
    case 504: return API_ERRORS.SERVER_ERROR
    // Everything else, and 422 above all: `detail` is an ARRAY there, and assigning it raw — which
    // is what the shared client did until this change — hands an object to whatever renders
    // `userMessage`. That is React error #31 and a blank screen, from the error path.
    default: return flattenDetail(data?.detail) || data?.message || API_ERRORS.SERVER_ERROR
  }
}

/**
 * Attach `userMessage` to a rejected request and re-reject it, with no other effect.
 *
 * For the module clients whose response interceptor was `(error) => Promise.reject(error)` — an
 * interceptor that existed and did nothing, while reading to the next person as though error
 * handling lived there.
 */
export function rejectWithUserMessage(error) {
  if (error && typeof error === 'object' && !error.userMessage) {
    error.userMessage = userMessageFor(error)
  }
  return Promise.reject(error)
}
